import { Fragment, use } from 'react';

import {
  AskUserQuestionComplete,
  AskUserQuestionError,
  AskUserQuestionSlides,
} from '#plugin-chat/components/tools/ask-user-question/AskUserQuestionTool.render.tsx';
import {
  type AskUserQuestionInput,
  AskUserQuestionInputSchema,
  type AskUserQuestionOutput,
  SLIDE_TYPE_META,
  SLIDE_TYPES,
  type StepperPayload,
} from '#plugin-chat/components/tools/ask-user-question/schema.ts';
import type { ToolProps } from '#plugin-chat/components/tools/Tool.ts';
import { ToolsContext } from '#plugin-chat/contexts/ToolsContext.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

/**
 * Unified tool for asking user questions via interactive slides.
 *
 * The full LLM-facing usage guidance (when-to/not-to-use,
 * follow-up-question patterns, option-description rules, slide-type
 * glossary) is carried on the `description` prop via a nested
 * `<ToolGuidance>` — `<Tool>` has no separate `instructions` channel,
 * so this is the only place it can live. `execute` runs the
 * slide-by-slide stepper flow against the human-in-the-loop callback
 * supplied by the AUI runtime; the `render` status map delegates to the
 * `AskUserQuestionSlides` / `AskUserQuestionComplete` /
 * `AskUserQuestionError` renderers for the per-status UI.
 */
export function AskUserQuestionTool(props: ToolProps) {
  const tools = use(ToolsContext);
  return (
    <Tool<AskUserQuestionInput, AskUserQuestionOutput>
      description={
        <ToolGuidance
          description="Ask the user a question via interactive slides."
          whenNotToUse={[
            'User just answered a question — do work first, then ask again if needed',
            'Simple factual question — answer directly without follow-up',
            'Only one entity is found — proceed directly without asking',
            'Only one option available — the tool requires minimum 2 options per slide, skip the question',
          ]}
          whenToUse={[
            'Multiple matches from query — let user select',
            'Ambiguous request — clarify intent with options derived from data',
            'After presenting data — offer drill-downs, comparisons, or related views',
            'Complex tasks (diagnosis, root cause analysis) — gather scope and parameters through guided multi-slide questions before executing',
          ]}
        >
          <h3>Option Descriptions</h3>
          <ul>
            <li>Include a description for each option unless the label is self-explanatory (e.g., "Yes", "No").</li>
            <li>
              For entities from queries, include relevant metadata: "Producer, Oil" or "Injector, Water, Offline".
            </li>
            <li>
              Provide minimum 2 options per slide; options must be context-specific based on the data just returned.
            </li>
          </ul>

          <h3>Slide Types</h3>
          <dl>
            {SLIDE_TYPES.map((type) => (
              <Fragment key={type}>
                <dt>{type}</dt>
                <dd>{SLIDE_TYPE_META[type].label}</dd>
              </Fragment>
            ))}
          </dl>
        </ToolGuidance>
      }
      execute={async (
        { slides }: AskUserQuestionInput,
        { human }: { human: (payload: unknown) => Promise<unknown> },
      ): Promise<AskUserQuestionOutput> => {
        // Collect responses for each slide
        const responses: Record<string, string[]> = {};

        for (const [i, slide] of slides.entries()) {
          // Create stepper payload for this slide
          const payload: StepperPayload = {
            type: 'stepper',
            slideIndex: i,
            totalSlides: slides.length,
            slide,
          };

          const response = (await human(payload)) as { selectedIds: string[] };
          responses[slide.title] = slide.options
            .filter((opt) => response.selectedIds.includes(opt.id))
            .map((opt) => opt.label);
        }

        return { success: true as const, data: responses };
      }}
      message={{
        // Dialogue-shaped transcript: an `assistant` turn (the
        // questions the model asked) followed by a `user` turn (the
        // answers, in the user's own voice). Emitting real turns keeps
        // the answer the latest `user` turn the model anchors on —
        // instead of being folded into a tool-call record behind the
        // original prompt, where the model mistook the stale prompt for
        // the live instruction.
        transcript: ({ args, outcome }) => (
          <>
            <transcript-message role="assistant">
              {tools.AskUserQuestion} tool was called with the following questions:
              <ol>
                {args.slides?.map((s, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: slide order is stable
                  <li key={i}>{s.question}</li>
                ))}
              </ol>
            </transcript-message>
            {outcome?.kind === 'result' && (
              <transcript-message role="user">
                <p>See my responses to your {tools.AskUserQuestion} tool call below:</p>
                <ol>
                  {Object.entries((outcome.output as AskUserQuestionOutput).data ?? {}).map(([title, choices], i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: response order matches slide order
                    <li key={i}>
                      {title}: {choices.join(', ')}
                    </li>
                  ))}
                </ol>
              </transcript-message>
            )}
            {(outcome?.kind === 'error' || outcome?.kind === 'denied') && (
              <transcript-message role="user">
                [tool {tools.AskUserQuestion} was called; The user opted to not provide an answer to the question.]
              </transcript-message>
            )}
          </>
        ),
      }}
      parameters={AskUserQuestionInputSchema}
      render={{
        // The slides UI mounts from `running` too — see AskUserQuestionSlides
        // for the upstream first-interrupt race this works around.
        running: () => <AskUserQuestionSlides />,
        awaitingInput: () => <AskUserQuestionSlides />,
        complete: ({ result }) => <AskUserQuestionComplete result={result} />,
        error: () => <AskUserQuestionError />,
        cancelled: () => <AskUserQuestionError />,
      }}
      {...props}
    />
  );
}
